import React, { useState } from 'react'
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Badge,
  Spinner,
} from 'reactstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator'
import { UseQueryResult } from 'react-query'
import { priceFormat } from '../../../../helpers/utils'
import {
  PaymentReserveDto,
  PaymentReservesResponse,
} from '../../../../api-dtos/support/merchant/payment-reserve.dto.interface'
import { ReservePlanDto } from '../../../../api-dtos/support/merchant/reserve-plan.dto.interface'
import Currency from '../../../../api-dtos/types/currency.type'

interface PaymentReservesTableProps {
  paymentReservesQuery: UseQueryResult<PaymentReservesResponse, unknown>
  reservePlan: ReservePlanDto | null
  tablePage: number
  tableSizePerPage: number
  onTableChange: (page: number, sizePerPage: number) => void
}

const PaymentReservesTable: React.FC<PaymentReservesTableProps> = ({
  paymentReservesQuery,
  reservePlan,
  tablePage,
  tableSizePerPage,
  onTableChange,
}) => {
  const [copiedPaymentId, setCopiedPaymentId] = useState<string | null>(null)

  const formatCurrency = (units: number, currencyCode: string) => {
    return priceFormat({ units, currencyCode: currencyCode as Currency })
  }

  const paymentReservesColumns = [
    {
      dataField: 'paymentId',
      text: 'Payment ID',
      headerFormatter: () => (
        <div>
          <div>Payment</div>
          <div>ID</div>
        </div>
      ),
      formatter: (cell: string) => (
        <code
          className="text-muted"
          style={{ cursor: 'pointer' }}
          title={
            copiedPaymentId === cell
              ? 'Copied!'
              : 'Click to copy full payment ID'
          }
          onClick={() => {
            navigator.clipboard.writeText(cell)
            setCopiedPaymentId(cell)
            setTimeout(() => setCopiedPaymentId(null), 2000)
          }}
        >
          {cell.substring(0, 8)}...
          <i
            className={`ms-1 ${copiedPaymentId === cell ? 'bx bx-check text-success' : 'bx bx-copy'}`}
            style={{ fontSize: '12px' }}
          ></i>
        </code>
      ),
      headerAlign: 'left',
      align: 'left',
    },
    {
      dataField: 'paymentTotalUnits',
      text: 'Original Amount',
      headerFormatter: () => (
        <div>
          <div>Original</div>
          <div>Amount</div>
        </div>
      ),
      formatter: (cell: number, row: PaymentReserveDto) => (
        <span className="text-dark">
          {formatCurrency(cell, row.currencyCode)}
        </span>
      ),
      headerAlign: 'left',
      align: 'left',
    },
    {
      dataField: 'reservedAmountUnits',
      text: 'Reserved Amount',
      headerFormatter: () => (
        <div>
          <div>Reserved</div>
          <div>Amount</div>
        </div>
      ),
      formatter: (cell: number, row: PaymentReserveDto) => (
        <span className="text-dark">
          {formatCurrency(cell, row.currencyCode)}
        </span>
      ),
      headerAlign: 'left',
      align: 'left',
    },
    {
      dataField: 'reservedAt',
      text: 'Reserved At',
      headerFormatter: () => (
        <div>
          <div>Reserved</div>
          <div>At</div>
        </div>
      ),
      formatter: (cell: string) => {
        const date = new Date(cell)
        return (
          <div className="text-dark">
            <div>{date.toLocaleDateString()}</div>
            <div style={{ fontSize: '0.85em', color: '#6c757d' }}>
              {date.toLocaleTimeString()}
            </div>
          </div>
        )
      },
      headerAlign: 'left',
      align: 'left',
    },
    {
      dataField: 'releaseDate',
      text: 'Release Date',
      headerFormatter: () => (
        <div>
          <div>Release</div>
          <div>Date</div>
        </div>
      ),
      formatter: (cell: string) => {
        const date = new Date(cell)
        return (
          <div className="text-dark">
            <div>{date.toLocaleDateString()}</div>
            <div style={{ fontSize: '0.85em', color: '#6c757d' }}>
              {date.toLocaleTimeString()}
            </div>
          </div>
        )
      },
      headerAlign: 'left',
      align: 'left',
    },
    {
      dataField: 'status',
      text: 'Status',
      headerFormatter: () => <div>Status</div>,
      formatter: (cell: string) => (
        <Badge
          className={`text-white ${cell === 'reserved' ? 'bg-danger' : 'bg-success'}`}
        >
          {cell.toUpperCase()}
        </Badge>
      ),
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'timeRemainingDays',
      text: 'Days Remaining',
      headerFormatter: () => (
        <div>
          <div>Days</div>
          <div>Remaining</div>
        </div>
      ),
      formatter: (cell: number, row: PaymentReserveDto) => (
        <span className="text-dark">
          {row.status === 'reserved' ? (
            <span>{cell} days</span>
          ) : (
            <span className="text-muted">N/A</span>
          )}
        </span>
      ),
      headerAlign: 'center',
      align: 'center',
    },
    {
      dataField: 'planPercentAtTime',
      text: 'Plan %',
      headerFormatter: () => (
        <div>
          <div>Plan %</div>
        </div>
      ),
      formatter: (cell: number) => <span className="text-dark">{cell}%</span>,
      headerAlign: 'center',
      align: 'center',
    },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="h4">Payment Reserves</CardTitle>
      </CardHeader>
      <CardBody>
        {paymentReservesQuery.isLoading ? (
          <div className="text-center">
            <Spinner />
            <p className="mt-2">Loading payment reserves...</p>
          </div>
        ) : paymentReservesQuery.error ? (
          <div className="text-center text-danger">
            <p>Failed to load payment reserves. Please try again.</p>
          </div>
        ) : (paymentReservesQuery.data?.data?.length || 0) > 0 ? (
          <BootstrapTable
            keyField="id"
            data={paymentReservesQuery.data?.data || []}
            columns={paymentReservesColumns}
            bordered={false}
            striped={false}
            hover
            classes="table align-middle table-nowrap"
            headerWrapperClasses="thead-light"
            pagination={paginationFactory({
              withFirstAndLast: false,
              sizePerPage: tableSizePerPage,
              page: tablePage,
              totalSize: paymentReservesQuery.data?.total || 0,
            })}
            onTableChange={(_, { page, sizePerPage }) => {
              if (tablePage !== page || tableSizePerPage !== sizePerPage) {
                onTableChange(page, sizePerPage)
              }
            }}
          />
        ) : (
          <div className="text-center text-muted">
            <p>No payment reserves found.</p>
            {reservePlan && (
              <p className="small">
                Payment reserves will appear here when payments are processed
                with the active reserve plan.
              </p>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

export default PaymentReservesTable
